import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CspUtilService } from '@appState/csp/csp-util/csp-util.service';
import { getFilterFromLocalStorage } from '@highlander/services/helpers';
import { GroupedRoles, RoleType, UpdateRoleForEntityRequest } from '@models/authorization';
import { DELETE_DIALOG_YES } from '@models/constants';
import { PartnerCustomer, PartnerCustomerListItem } from '@models/customers';
import {
  CreateIndirectProviderRequest,
  IndirectProvider,
  Partner,
  PartnerAdminUser,
  PartnerBusinessData,
  PartnerGroup,
  PartnerListItem,
  PartnerToggleOptions,
  SetPartnerMicrosoftInfoRequest,
  UpdatePartnerRequest,
} from '@models/partners';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ConfirmationDialogData, CuiConfirmationDialogComponent, DialogService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { PARTNERS_TABLE_ID } from 'src/app/app-pages/partners/partners.component';
import { CustomersService } from '../customers/customers.service';
import {
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  StateDictionary,
  updateEntity,
} from '../helpers';
import { PartnersService } from './partners.service';

type PartnersState = {
  isLoading: boolean;
  partners: PartnerListItem[];
  toggle: PartnerToggleOptions;

  isSelectedPartnerLoading: boolean;
  selectedPartner: Partner | null;

  isBusinessDataLoading: boolean;
  partnerBusinessData: PartnerBusinessData | null;

  isRolesLoading: boolean;
  partnerRoles: GroupedRoles[];
  isRolesValidationLoading: boolean;
  partnerRolesInUse: boolean | null;

  isPartnerAdminUsersLoading: boolean;
  partnerAdminUsers: PartnerAdminUser[];

  isIndirectProvidersLoading: boolean;
  indirectProviders: IndirectProvider[];
  isSelectedIndirectProviderLoading: boolean;
  selectedIndirectProvider: IndirectProvider | null;
  isIndirectProviderCreateLoading: boolean;

  partnerCustomers: PartnerCustomerListItem[];
  isPartnerCustomersLoading: boolean;
  selectedPartnerCustomer: PartnerCustomer | null;
  isSelectedPartnerCustomerLoading: boolean;

  allowedDomains: string[];
  isAllowedDomainsLoading: boolean;

  partnerCustomerGroups: PartnerGroup[];
  isPartnerCustomerGroupsLoading: boolean;

  partnerEnvironmentGroups: PartnerGroup[];
  isPartnerEnvironmentGroupsLoading: boolean;
};

const initialState: PartnersState = {
  isLoading: false,
  partners: [],
  toggle: PartnerToggleOptions.Operative,

  isSelectedPartnerLoading: false,
  selectedPartner: null,

  isBusinessDataLoading: false,
  partnerBusinessData: null,

  isRolesLoading: false,
  partnerRoles: [],
  isRolesValidationLoading: false,
  partnerRolesInUse: null,

  isPartnerAdminUsersLoading: false,
  partnerAdminUsers: [],

  isIndirectProvidersLoading: false,
  indirectProviders: [],
  isSelectedIndirectProviderLoading: false,
  selectedIndirectProvider: null,
  isIndirectProviderCreateLoading: false,

  partnerCustomers: [],
  isPartnerCustomersLoading: false,
  selectedPartnerCustomer: null,
  isSelectedPartnerCustomerLoading: false,

  allowedDomains: [],
  isAllowedDomainsLoading: false,

  partnerCustomerGroups: [],
  isPartnerCustomerGroupsLoading: false,

  partnerEnvironmentGroups: [],
  isPartnerEnvironmentGroupsLoading: false,
};

export const partnersDictionary: StateDictionary<Partner> = {
  entityList: 'partners',
  listLoading: 'isLoading',
  selectedEntity: 'selectedPartner',
  selectedEntityLoading: 'isSelectedPartnerLoading',
  entityId: 'id',
  createLoading: '',
  config: { skipListUpdate: true },
};

export const PartnersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('partners-store'),

  withComputed(store => ({
    selectedPartnerId: computed(() => store.selectedPartner()?.id || null),
    selectedIndirectProviderId: computed(() => store.selectedIndirectProvider()?.cspIndirectProviderTenantId || null),
    selectedPartnerCustomerId: computed(() => store.selectedPartnerCustomer()?.customerId || null),
  })),

  withMethods(
    (
      store,
      partnersService = inject(PartnersService),
      customersService = inject(CustomersService),
      router = inject(Router),
      dialogService = inject(DialogService),
      cspUtilService = inject(CspUtilService),
    ) => {
      createStateResetHandler<PartnersState>('/partners')(store, initialState);
      createCustomStateSetOnUrlHandler<PartnersState>('/partners', { selectedPartner: null })(store);
      createCustomStateSetOnRegexHandler<PartnersState>(new RegExp(`^/partners/(${guidRegex})/customers$`))(store, {
        selectedPartnerCustomer: null,
      });

      const savedFilter = getFilterFromLocalStorage(PARTNERS_TABLE_ID);
      if (savedFilter?.toggleSearch) {
        patchState(store, {
          toggle: savedFilter.toggleSearch === 'All' ? PartnerToggleOptions.All : PartnerToggleOptions.Operative,
        });
      }

      const partnerMethods = {
        getAll: getAll<PartnerListItem, boolean | undefined>({
          store,
          service: partnersService,
          dictionary: partnersDictionary as unknown as StateDictionary<PartnerListItem>,
        }),

        getOne: getOne<Partner>({
          store,
          service: partnersService,
          dictionary: partnersDictionary,
        }),

        updatePartner: updateEntity<Partner, UpdatePartnerRequest>({
          store,
          service: partnersService,
          dictionary: partnersDictionary,
          onSuccessCallback: partner => {
            patchState(store, { selectedPartner: partner });
          },
        }),

        getPartnerBusinessData: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isBusinessDataLoading: true })),
            switchMap(id =>
              partnersService.getPartnerBusinessData(id).pipe(
                tap(businessData => {
                  patchState(store, {
                    isBusinessDataLoading: false,
                    partnerBusinessData: businessData,
                  });
                }),
                catchError(() => {
                  patchState(store, { isBusinessDataLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        setCrmAccId: rxMethod<{ partnerId: string; crmAccountId: string }>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedPartnerLoading: true })),
            switchMap(({ partnerId, crmAccountId }) =>
              partnersService.setCrmAccId(partnerId, crmAccountId).pipe(
                tap(partner => {
                  patchState(store, {
                    isLoading: false,
                    isSelectedPartnerLoading: false,
                    selectedPartner: partner,
                  });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedPartnerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        markForDeletion: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isSelectedPartnerLoading: true })),
            switchMap(id =>
              partnersService.markForDeletion(id).pipe(
                tap(() => {
                  patchState(store, {
                    isSelectedPartnerLoading: false,
                    selectedPartner: null,
                  });
                  router.navigate(['partners']);
                  utilityMethods.loadPartners();
                }),
                catchError(() => {
                  patchState(store, { isSelectedPartnerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        activatePartner: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedPartnerLoading: true })),
            switchMap(partnerId =>
              partnersService.activatePartner(partnerId).pipe(
                tap(partner => {
                  const updatedListItem = mapPartnerResponseToListItem(partner);
                  patchState(store, {
                    isLoading: false,
                    isSelectedPartnerLoading: false,
                    partners: findAndReplace<PartnerListItem>(store.partners(), updatedListItem, item => item.id === partner.id),
                    selectedPartner: partner,
                  });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedPartnerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deactivatePartner: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedPartnerLoading: true })),
            switchMap(partnerId =>
              partnersService.deactivatePartner(partnerId).pipe(
                tap(partner => {
                  const updatedListItem = mapPartnerResponseToListItem(partner);
                  patchState(store, {
                    isLoading: false,
                    isSelectedPartnerLoading: false,
                    partners: findAndReplace<PartnerListItem>(store.partners(), updatedListItem, item => item.id === partner.id),
                    selectedPartner: partner,
                  });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedPartnerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        refreshCustomers: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isSelectedPartnerLoading: true })),
            switchMap(partnerId =>
              cspUtilService.refreshPartnerCustomers(partnerId).pipe(
                tap(() => {
                  patchState(store, { isSelectedPartnerLoading: false });
                  dialogService.openDialog<ConfirmationDialogData, undefined>(CuiConfirmationDialogComponent, {
                    title: 'Refresh customers',
                    description:
                      'Request to refresh customers for partner added to queue. Updated data will be visible once request completes successfully.',
                    cancelText: DELETE_DIALOG_YES,
                    confirmText: '',
                    showCloseBtn: false,
                  });
                  partnerMethods.getOne(partnerId);
                }),
                catchError(() => {
                  patchState(store, { isSelectedPartnerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        setMicrosoftInfo: rxMethod<{ partnerId: string; request: SetPartnerMicrosoftInfoRequest | {} }>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedPartnerLoading: true })),
            switchMap(({ partnerId, request }) =>
              partnersService.setMicrosoftInfo(partnerId, request).pipe(
                tap(partner => {
                  const updatedListItem = mapPartnerResponseToListItem(partner);
                  patchState(store, {
                    isLoading: false,
                    isSelectedPartnerLoading: false,
                    partners: findAndReplace<PartnerListItem>(store.partners(), updatedListItem, item => item.id === partner.id),
                    selectedPartner: partner,
                  });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedPartnerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const roleMethods = {
        getPartnerRoles: rxMethod<{ partnerId: string; filter: RoleType }>(
          pipe(
            tap(() => patchState(store, { isRolesLoading: true, partnerRoles: [] })),
            switchMap(({ partnerId, filter }) =>
              partnersService.getPartnerRoles(partnerId, filter).pipe(
                tap(roles => {
                  patchState(store, {
                    isRolesLoading: false,
                    partnerRoles: roles,
                  });
                }),
                catchError(() => {
                  patchState(store, { isRolesLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        validatePartnerRoles: rxMethod<{ partnerId: string; roles: UpdateRoleForEntityRequest[] }>(
          pipe(
            tap(() => patchState(store, { isRolesValidationLoading: true })),
            switchMap(({ partnerId, roles }) =>
              partnersService.validatePartnerRoles(partnerId, roles).pipe(
                tap(partnerRolesInUse => {
                  patchState(store, { isRolesValidationLoading: false, partnerRolesInUse });
                }),
                catchError(() => {
                  patchState(store, { isRolesValidationLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        clearPartnerRolesValidation: (): void => {
          patchState(store, { partnerRolesInUse: null });
        },

        setPartnerRoles: rxMethod<{ partnerId: string; roles: UpdateRoleForEntityRequest[]; filter: RoleType; forceUpdate?: boolean }>(
          pipe(
            tap(() => patchState(store, { isRolesLoading: true })),
            switchMap(({ partnerId, roles, filter, forceUpdate }) =>
              partnersService.setPartnerRoles(partnerId, roles, filter, forceUpdate).pipe(
                tap(updatedRoles => {
                  const urlMatchesType =
                    (filter === 'Internal' && router.url.includes('internal-roles')) ||
                    (filter === 'Regular' && !router.url.includes('internal-roles'));
                  if (urlMatchesType) {
                    patchState(store, {
                      isRolesLoading: false,
                      partnerRoles: updatedRoles,
                    });
                  }
                }),
                catchError(() => {
                  patchState(store, { isRolesLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const adminUserMethods = {
        getAdminUsers: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isPartnerAdminUsersLoading: true })),
            switchMap(partnerId =>
              partnersService.getAdminUsers(partnerId).pipe(
                tap(admins => {
                  patchState(store, {
                    isPartnerAdminUsersLoading: false,
                    partnerAdminUsers: admins.sort((a, b) => a.firstName.localeCompare(b.firstName)),
                  });
                }),
                catchError(() => {
                  patchState(store, { isPartnerAdminUsersLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const indirectProviderMethods = {
        getIndirectProviders: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { indirectProviders: [], isIndirectProvidersLoading: true })),
            switchMap(partnerId =>
              partnersService.getAllIndirectProviders(partnerId).pipe(
                tap(indirectProviders => {
                  patchState(store, {
                    isIndirectProvidersLoading: false,
                    indirectProviders,
                  });
                }),
                catchError(() => {
                  patchState(store, { isIndirectProvidersLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getOneIndirectProvider: rxMethod<{ partnerId: string; indirectProviderId: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedIndirectProviderLoading: true })),
            switchMap(({ partnerId, indirectProviderId }) => {
              return partnersService.getOneIndirectProvider(partnerId, indirectProviderId).pipe(
                tap(indirectProvider => {
                  patchState(store, {
                    isSelectedIndirectProviderLoading: false,
                    selectedIndirectProvider: indirectProvider,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedIndirectProviderLoading: false });
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        createIndirectProvider: rxMethod<{ partnerId: string; req: CreateIndirectProviderRequest }>(
          pipe(
            tap(() =>
              patchState(store, {
                isIndirectProvidersLoading: true,
                isIndirectProviderCreateLoading: true,
              }),
            ),
            switchMap(({ partnerId, req }) =>
              partnersService.createIndirectProvider(partnerId, req).pipe(
                tap(indirectProvider => {
                  patchState(store, {
                    isIndirectProvidersLoading: false,
                    isIndirectProviderCreateLoading: false,
                    indirectProviders: [...store.indirectProviders(), indirectProvider],
                    selectedIndirectProvider: indirectProvider,
                  });
                  router.navigate(['partners', partnerId, 'indirect-providers', indirectProvider.cspIndirectProviderTenantId, 'details']);
                }),
                catchError(() => {
                  patchState(store, {
                    isIndirectProvidersLoading: false,
                    isIndirectProviderCreateLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteIndirectProvider: rxMethod<{ partnerId: string; indirectProviderId: string }>(
          pipe(
            tap(() => patchState(store, { isIndirectProvidersLoading: true })),
            switchMap(({ partnerId, indirectProviderId }) =>
              partnersService.deleteIndirectProvider(partnerId, indirectProviderId).pipe(
                tap(() => {
                  patchState(store, {
                    isIndirectProvidersLoading: false,
                    selectedIndirectProvider: null,
                  });
                  router.navigate(['partners', partnerId, 'indirect-providers']);
                  indirectProviderMethods.getIndirectProviders(partnerId);
                }),
                catchError(() => {
                  patchState(store, { isIndirectProvidersLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const customerMethods = {
        getPartnerCustomers: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isPartnerCustomersLoading: true })),
            switchMap(partnerId =>
              customersService.getPartnerCustomers(partnerId).pipe(
                tap(customers => {
                  patchState(store, {
                    isPartnerCustomersLoading: false,
                    partnerCustomers: customers,
                  });
                }),
                catchError(() => {
                  patchState(store, { isPartnerCustomersLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getPartnerCustomer: rxMethod<{ customerId: string; partnerId: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedPartnerCustomerLoading: true })),
            switchMap(({ customerId, partnerId }) =>
              customersService.getPartnerCustomer(customerId, partnerId).pipe(
                tap(customer => {
                  patchState(store, {
                    isSelectedPartnerCustomerLoading: false,
                    selectedPartnerCustomer: customer,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedPartnerCustomerLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const allowedDomainMethods = {
        getAllowedDomains: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isAllowedDomainsLoading: true })),
            switchMap(partnerId =>
              partnersService.getAllowedDomains(partnerId).pipe(
                tap(response => {
                  patchState(store, {
                    isAllowedDomainsLoading: false,
                    allowedDomains: response.allowedDomains.sort((a, b) => a.localeCompare(b)),
                  });
                }),
                catchError(() => {
                  patchState(store, { isAllowedDomainsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const groupMethods = {
        getPartnerCustomerGroups: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isPartnerCustomerGroupsLoading: true })),
            switchMap(partnerId =>
              partnersService.getPartnerCustomerGroups(partnerId).pipe(
                tap(groups => {
                  patchState(store, {
                    isPartnerCustomerGroupsLoading: false,
                    partnerCustomerGroups: groups.sort((a, b) => a.name.localeCompare(b.name)),
                  });
                }),
                catchError(() => {
                  patchState(store, { isPartnerCustomerGroupsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getPartnerEnvironmentGroups: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isPartnerEnvironmentGroupsLoading: true })),
            switchMap(partnerId =>
              partnersService.getPartnerEnvironmentGroups(partnerId).pipe(
                tap(groups => {
                  patchState(store, {
                    isPartnerEnvironmentGroupsLoading: false,
                    partnerEnvironmentGroups: groups.sort((a, b) => a.name.localeCompare(b.name)),
                  });
                }),
                catchError(() => {
                  patchState(store, { isPartnerEnvironmentGroupsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationMethods = {
        goToPartnerList: (): void => {
          patchState(store, { selectedPartner: null });
          router.navigate(['partners']);
        },

        goToIndirectProviderActions: (partnerId: string, indirectProviderId: string): void => {
          indirectProviderMethods.getOneIndirectProvider({ partnerId, indirectProviderId });
          router.navigate(['partners', partnerId, 'indirect-providers', indirectProviderId]);
        },

        goToIndirectProviderListPage: (partnerId: string): void => {
          patchState(store, { selectedIndirectProvider: null });
          router.navigate(['partners', partnerId, 'indirect-providers']);
        },

        goToPartnerActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['partners', id]);
          if (!result) return false;

          patchState(store, {
            selectedIndirectProvider: null,
            partnerAdminUsers: [],
            partnerCustomers: [],
            selectedPartnerCustomer: null,
            indirectProviders: [],
            allowedDomains: [],
            partnerCustomerGroups: [],
            partnerEnvironmentGroups: [],
          });

          return true;
        },

        goToPartnerCustomerActions: (partnerId: string, customerId: string): void => {
          customerMethods.getPartnerCustomer({ customerId, partnerId });
          router.navigate(['partners', partnerId, 'customers', customerId]);
        },
      };

      const utilityMethods = {
        loadPartners: (toggle?: PartnerToggleOptions) => {
          if (toggle !== undefined) {
            patchState(store, { toggle });
          }
          const currentToggle = store.toggle();
          partnerMethods.getAll(currentToggle === PartnerToggleOptions.All);
        },

        resetState: () => {
          patchState(store, {
            selectedPartner: null,
            partnerRoles: [],
            partnerBusinessData: null,
            partnerAdminUsers: [],
            indirectProviders: [],
            selectedIndirectProvider: null,
            partnerCustomers: [],
            selectedPartnerCustomer: null,
            allowedDomains: [],
            partnerCustomerGroups: [],
            partnerEnvironmentGroups: [],
          });
        },
      };

      return {
        ...partnerMethods,
        ...roleMethods,
        ...adminUserMethods,
        ...indirectProviderMethods,
        ...navigationMethods,
        ...utilityMethods,
        ...customerMethods,
        ...allowedDomainMethods,
        ...groupMethods,
      };
    },
  ),
);

export interface PartnersStoreType {
  readonly isLoading: Signal<boolean>;
  readonly partners: Signal<PartnerListItem[]>;
  readonly toggle: Signal<'Operative' | 'All'>;
  readonly isSelectedPartnerLoading: Signal<boolean>;
  readonly selectedPartner: Signal<Partner | null>;
  readonly isBusinessDataLoading: Signal<boolean>;
  readonly partnerBusinessData: Signal<PartnerBusinessData | null>;
  readonly isRolesLoading: Signal<boolean>;
  readonly partnerRoles: Signal<GroupedRoles[]>;
  readonly isRolesValidationLoading: Signal<boolean>;
  readonly partnerRolesInUse: Signal<boolean | null>;
  readonly isPartnerAdminUsersLoading: Signal<boolean>;
  readonly partnerAdminUsers: Signal<PartnerAdminUser[]>;
  readonly isIndirectProvidersLoading: Signal<boolean>;
  readonly indirectProviders: Signal<IndirectProvider[]>;
  readonly isSelectedIndirectProviderLoading: Signal<boolean>;
  readonly selectedIndirectProvider: Signal<IndirectProvider | null>;
  readonly isIndirectProviderCreateLoading: Signal<boolean>;
  readonly partnerCustomers: Signal<PartnerCustomerListItem[]>;
  readonly isPartnerCustomersLoading: Signal<boolean>;
  readonly selectedPartnerCustomer: Signal<PartnerCustomer | null>;
  readonly isSelectedPartnerCustomerLoading: Signal<boolean>;
  readonly allowedDomains: Signal<string[]>;
  readonly isAllowedDomainsLoading: Signal<boolean>;

  readonly partnerCustomerGroups: Signal<PartnerGroup[]>;
  readonly isPartnerCustomerGroupsLoading: Signal<boolean>;
  readonly partnerEnvironmentGroups: Signal<PartnerGroup[]>;
  readonly isPartnerEnvironmentGroupsLoading: Signal<boolean>;

  readonly selectedPartnerId: Signal<string | null>;
  readonly selectedIndirectProviderId: Signal<string | null>;
  readonly selectedPartnerCustomerId: Signal<string | null>;

  readonly getAll: (input: boolean | undefined) => void;
  readonly getOne: (id: string) => void;
  readonly updatePartner: (params: { id: string; dto: UpdatePartnerRequest }) => void;
  readonly getPartnerBusinessData: (id: string) => void;
  readonly setCrmAccId: (params: { partnerId: string; crmAccountId: string }) => void;
  readonly markForDeletion: (id: string) => void;
  readonly activatePartner: (partnerId: string) => void;
  readonly deactivatePartner: (partnerId: string) => void;
  readonly refreshCustomers: (partnerId: string) => void;
  readonly setMicrosoftInfo: (params: { partnerId: string; request: SetPartnerMicrosoftInfoRequest | {} }) => void;
  readonly getPartnerRoles: (params: { partnerId: string; filter: RoleType }) => void;
  readonly setPartnerRoles: (params: { partnerId: string; roles: UpdateRoleForEntityRequest[]; filter: RoleType; forceUpdate?: boolean }) => void;
  readonly validatePartnerRoles: (params: { partnerId: string; roles: UpdateRoleForEntityRequest[] }) => void;
  readonly clearPartnerRolesValidation: () => void;
  readonly getAdminUsers: (partnerId: string) => void;
  readonly getIndirectProviders: (partnerId: string) => void;
  readonly getOneIndirectProvider: (params: { partnerId: string; indirectProviderId: string }) => void;
  readonly createIndirectProvider: (params: { partnerId: string; req: CreateIndirectProviderRequest }) => void;
  readonly deleteIndirectProvider: (params: { partnerId: string; indirectProviderId: string }) => void;
  readonly getPartnerCustomers: (partnerId: string) => void;
  readonly getPartnerCustomer: (params: { customerId: string; partnerId: string }) => void;
  readonly getAllowedDomains: (partnerId: string) => void;
  readonly getPartnerCustomerGroups: (partnerId: string) => void;
  readonly getPartnerEnvironmentGroups: (partnerId: string) => void;
  readonly goToPartnerActions: (id: string) => Promise<boolean>;
  readonly goToPartnerList: () => void;
  readonly goToIndirectProviderActions: (partnerId: string, indirectProviderId: string) => void;
  readonly goToIndirectProviderListPage: (partnerId: string) => void;
  readonly resetState: () => void;
  readonly loadPartners: (toggle?: PartnerToggleOptions) => void;
  readonly goToPartnerCustomerActions: (partnerId: string, customerId: string) => void;
}

function mapPartnerResponseToListItem(partner: Partner): PartnerListItem {
  return {
    id: partner.id,
    companyName: partner.details.companyName,
    country: partner.details.country,
    region: partner.details.region,
    membershipLevel: partner.details.membershipLevel,
    mpnId: partner.details.mpnId,
    lastUpdate: new Date(partner.details.lastUpdate),
    domainName: partner.details.domainName,
    status: partner.details.status,
    crmAccountId: partner.details.crmAccountId,
    entraTenantId: partner.details.entraTenantId,
    expectedTenantId: partner.details.expectedTenantId,
    applicationActivated: partner.details.applicationActivated,
  };
}
